import api from './api';
import { performLogout } from './logoutService';
import { STORAGE_KEYS } from '../utils/storageKeys';

// Define interface for user profile response from backend
export interface UserProfileResponse {
  user: {
    id: string;
    username: string;
    email: string;
    first_name?: string;
    last_name?: string;
    is_active: boolean;
    company?: string;
    auth0_user_id?: string;
    invitation_sent_at?: string | null;
    invitation_accepted_at?: string | null;
  };
  access_token: string;
  expires_in: number;
  expires_at: number;
  issued_at: number;
  token_type: string;
}

// Token refresh buffer (in milliseconds) - refresh token 5 minutes before expiry
const TOKEN_REFRESH_BUFFER = 5 * 60 * 1000;

export type TokenProvider = () => Promise<string>;
let globalTokenProvider: TokenProvider | null = null;

// Global variable to store the extended token
let globalExtendedToken: string | null = null;

let refreshInterval: ReturnType<typeof setInterval> | null = null;

let lastAuthErrorType: string | null = null;
export const getLastAuthErrorType = (): string | null => lastAuthErrorType;

// Global singleton to ensure we only make one API call
class AppAuthService {
  private static instance: AppAuthService;
  private apiCallPromise: Promise<UserProfileResponse | null> | null = null;
  private apiCallResult: UserProfileResponse | null = null;
  private apiCallInProgress = false;

  private constructor() {}

  public static getInstance(): AppAuthService {
    if (!AppAuthService.instance) {
      AppAuthService.instance = new AppAuthService();
    }
    return AppAuthService.instance;
  }

  public async getUserProfileWithAccessToken(accessToken: string, forceRefresh = false): Promise<UserProfileResponse | null> {
    // Check if we have the extended token in localStorage
    const extendedToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);

    // If we already have the result and the extended token exists in localStorage, return the cached result
    if (!forceRefresh && this.apiCallResult && extendedToken) {
      return this.apiCallResult;
    }

    // If the API call is in progress, return the existing promise (unless forceRefresh)
    if (!forceRefresh && this.apiCallInProgress && this.apiCallPromise) {
      return this.apiCallPromise;
    }

    // Otherwise, make the API call
    this.apiCallInProgress = true;
    this.apiCallResult = null; // Clear cached result on force refresh or new call

    this.apiCallPromise = new Promise<UserProfileResponse | null>((resolve) => {
      api.get<UserProfileResponse>('/auth0/me/', {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      })
      .then((response) => {
        this.apiCallResult = response.data;

        // Set extended token immediately in memory and localStorage
        setExtendedToken(response.data.access_token);
        localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, response.data.access_token);

        // Store expiry info (convert seconds → milliseconds) & schedule background refresh
        if (response.data.expires_at) {
          const expiresAtMs = response.data.expires_at * 1000;
          storeTokenExpiry(expiresAtMs);
          scheduleTokenRefresh();
        } else if (response.data.expires_in) {
          const expiresAtMs = Date.now() + (response.data.expires_in * 1000);
          storeTokenExpiry(expiresAtMs);
          scheduleTokenRefresh();
        }

        lastAuthErrorType = null;
        resolve(this.apiCallResult);
      })
      .catch((error: unknown) => {
        console.warn('Auth0 token exchange: API call failed:', error);
        // Return null and let the caller decide how to handle the failure
        this.apiCallResult = null;
        lastAuthErrorType = (error as any)?.response?.data?.error_type ?? null;
        resolve(null);
      })
      .finally(() => {
        this.apiCallInProgress = false;
      });
    });

    return this.apiCallPromise;
  }

  public reset(): void {
    this.apiCallPromise = null;
    this.apiCallResult = null;
    this.apiCallInProgress = false;
    // Clear all token-related information from localStorage
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRES_AT);
    // Reset the global extended token
    globalExtendedToken = null;

    stopTokenRefresh();
  }
}

// Export a function that uses the singleton
export const getUserProfileWithAccessToken = async (accessToken: string, forceRefresh = false): Promise<UserProfileResponse | null> => {
  return AppAuthService.getInstance().getUserProfileWithAccessToken(accessToken, forceRefresh);
};

// Bounded retry tolerance around the refresh/token-exchange call only — a single
// transient network blip on this call should not force an immediate logout.
const REFRESH_RETRY_ATTEMPTS = 2;
const REFRESH_RETRY_DELAY_MS = 500;

export const refreshExtendedToken = async (accessToken: string): Promise<UserProfileResponse | null> => {
  for (let attempt = 1; attempt <= REFRESH_RETRY_ATTEMPTS; attempt++) {
    const result = await getUserProfileWithAccessToken(accessToken, true);
    if (result?.access_token) return result;
    if (attempt < REFRESH_RETRY_ATTEMPTS) {
      await new Promise((resolve) => setTimeout(resolve, REFRESH_RETRY_DELAY_MS));
    }
  }
  return null;
};

// Register Auth0's getAccessTokenSilently so the refresh timer can obtain fresh Auth0 tokens
export const setTokenProvider = (provider: TokenProvider): void => {
  globalTokenProvider = provider;
};

// Export a function to get the extended token
export const getExtendedToken = (): string | null => {
  return globalExtendedToken;
};

// Export a function to set the extended token
export const setExtendedToken = (token: string): void => {
  globalExtendedToken = token;
};

// Export a function to reset the singleton
export const resetAppAuthService = (): void => {
  AppAuthService.getInstance().reset();
};

// Export a function to store token expiry time (always in milliseconds)
export const storeTokenExpiry = (expiresAtMs: number): void => {
  localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRES_AT, expiresAtMs.toString());
};

// Background refresh: check every 60 s, refresh when within 5-minute buffer
export const scheduleTokenRefresh = (): void => {
  if (refreshInterval) clearInterval(refreshInterval);

  refreshInterval = setInterval(async () => {
    const expiresAtStr = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRES_AT);
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (!expiresAtStr || !token) return;

    const expiresAtMs = parseInt(expiresAtStr, 10);
    const nowMs = Date.now();

    if (expiresAtMs - nowMs <= TOKEN_REFRESH_BUFFER) {
      console.info('🔄 Token expiring soon, attempting refresh...');

      if (!globalTokenProvider) {
        console.warn('⚠️ No Auth0 token provider registered — cannot refresh silently.');
        return;
      }

      try {
        const auth0Token = await globalTokenProvider();
        const response = await refreshExtendedToken(auth0Token);
        if (response?.access_token) {
          console.info('✅ Token refreshed successfully');
        } else {
          console.error('❌ Refresh returned no token');
          if (expiresAtMs - Date.now() < 60 * 1000) {
            console.error('❌ Token nearly expired and refresh failed — logging out');
            performLogout();
          }
        }
      } catch (err) {
        console.error('❌ Silent refresh failed:', err);
      }
    }
  }, 60 * 1000);
};

export const stopTokenRefresh = (): void => {
  if (refreshInterval) {
    clearInterval(refreshInterval);
    refreshInterval = null;
  }
};

// On app load: restore existing token from localStorage and start refresh timer
export const initializeAppAuthService = (): void => {
  const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  const expiresAtStr = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRES_AT);

  if (token && expiresAtStr) {
    const expiresAtMs = parseInt(expiresAtStr, 10);
    if (expiresAtMs > Date.now()) {
      console.info('🔐 Restoring existing token and initializing silent refresh...');
      setExtendedToken(token);
      scheduleTokenRefresh();
    } else {
      console.warn('⚠️ Stored token expired — clearing...');
      resetAppAuthService();
    }
  } else {
    console.info('ℹ️ No stored token found — user likely not logged in yet.');
  }
};

// Export a function to check if the extended token exists and is valid
export const hasValidExtendedToken = (): boolean => {
  const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);

  if (!token || !globalExtendedToken) {
    return false;
  }

  try {
    const expiresAtStr = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRES_AT);
    if (!expiresAtStr) {
      return true; // No expiry info — assume valid; will be refreshed on next API call
    }

    const expiresAtMs = parseInt(expiresAtStr, 10);
    const nowMs = Date.now();

    // Both values are in milliseconds
    return expiresAtMs > (nowMs + TOKEN_REFRESH_BUFFER);
  } catch (error) {
    console.error('Error checking token validity:', error);
    return false;
  }
};
